import {
    trackCommandError,
    trackEvent,
    trackGaCommand,
    type AnalyticsSource,
} from "../analytics.js";
import {
    getLsDisplay,
    isCoreutilsCommand,
    isCoreutilsErrorOutput,
    isLsCommand,
    resolveCatImages,
    runCoreutils,
} from "../shell/coreutils.js";
import { isDownloadCommand, runDownloadCommand } from "../shell/download-utils.js";
import { getCommandBlock, type PasswordPromptConfig } from "../shell/denied-commands.js";
import { isUnameCommand, runUname } from "../shell/uname.js";
import { expandAlias } from "../shell/shell-aliases.js";
import type { TerminalSession } from "../shell/session.js";
import type { VfsSession } from "../shell/vfs.js";
import type { AppData, Command } from "../types.js";
import { COLORS } from "./constants.js";
import type { TerminalImageDisplay } from "./image-display.js";
import type { TerminalOutputView } from "./output-view.js";

export interface CommandHandlerDeps {
    appData: AppData;
    vfs: VfsSession;
    session: TerminalSession;
    commands: Record<string, Command>;
    output: TerminalOutputView;
    images: TerminalImageDisplay;
    shellPrompt: () => string;
    unknownCommandMessage: () => string;
    enterPasswordMode: (config: PasswordPromptConfig) => void;
    reboot: () => void;
    clearScreen: () => void;
    triggerDownload: (url: string, filename: string) => void;
    playError: () => void;
    playClick: () => void;
    updatePrompt: () => void;
    onCommand?: (input: string, command: string) => void;
    source: AnalyticsSource;
}

export function handleTerminalCommand(deps: CommandHandlerDeps, raw: string | Command): void {
    const trimmed = String(raw).trim();
    if (!trimmed) return;

    const command = expandAlias(trimmed, deps.appData.aliases) ?? trimmed;
    const value = command.toLowerCase();

    deps.onCommand?.(trimmed, command);
    trackGaCommand(command, deps.source);

    deps.output.scrollPinBottom = true;

    deps.output.appendLine(`${deps.shellPrompt()} ${trimmed}`, { color: COLORS.dim }, true);

    const cmd = value.split(/\s+/)[0]!;
    if (cmd === "reboot") {
        trackEvent("reboot");
        deps.reboot();
        return;
    }

    const block = getCommandBlock(value);
    if (block?.type === "password") {
        trackEvent("password_prompt", { cmd });
        deps.enterPasswordMode(block.config);
        return;
    }

    if (block?.type === "lines") {
        deps.output.appendLines(block.lines, { color: COLORS.dim });
        trackCommandError(cmd, "denied");
        deps.playError();
        return;
    }

    if (isUnameCommand(value)) {
        const profile = deps.session.getUnameProfile(deps.appData.config.hostname);
        deps.output.appendLine(runUname(value, profile));
        return;
    }

    if (isDownloadCommand(value)) {
        const result = runDownloadCommand(command, deps.vfs);
        deps.output.appendLines(result.lines, { color: COLORS.dim });
        if (result.isError) {
            trackCommandError(cmd, "download");
            deps.playError();
        } else if (result.job) {
            deps.playClick();
            deps.triggerDownload(result.job.url, result.job.filename);
        }
        deps.updatePrompt();
        return;
    }

    if (isCoreutilsCommand(value)) {
        if (isLsCommand(value)) {
            const display = getLsDisplay(command, deps.vfs);
            deps.output.appendLsDisplay(display, deps.vfs);
            if (display.type === "error") {
                trackCommandError(cmd, "coreutils");
                deps.playError();
            }
        } else {
            const catImages = resolveCatImages(command, deps.vfs);
            if (catImages) {
                deps.output.appendLine("");
                for (const src of catImages.sources) {
                    if (catImages.enhanced) {
                        deps.output.buffer.feedRun(() => deps.images.displayEnhancedImage(src));
                    } else {
                        deps.output.buffer.feedRun(() => deps.images.displayScanlineImage(src));
                    }
                }
                deps.output.buffer.feedRun(() => deps.updatePrompt());
                return;
            }

            const lines = runCoreutils(command, deps.vfs);

            if (lines.length > 0) {
                const isCat = cmd === "cat" && !isCoreutilsErrorOutput(lines);
                if (isCat) {
                    deps.output.appendLine("");
                    deps.output.appendCatLines(lines);
                } else {
                    deps.output.appendLines(lines);
                }
                if (isCoreutilsErrorOutput(lines)) {
                    trackCommandError(cmd, "coreutils");
                    deps.playError();
                }
            }
        }
        deps.updatePrompt();
        return;
    }

    const action = deps.commands[value];
    if (!action) {
        deps.output.appendLine(deps.unknownCommandMessage(), { color: COLORS.dim });
        trackCommandError(cmd, "unknown");
        deps.playError();
        return;
    }

    if (action === "clear") {
        trackEvent("clear_screen");
        deps.clearScreen();
        return;
    }

    if (action === "whoami") {
        deps.output.appendLine(deps.appData.config.handle);
    }
}
